import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { createProductSchema } from '@lombok/shared';
import type { CreateProductInput } from '@lombok/shared';
import { Button, Card, CardContent } from '@lombok/ui';
import { PageHeader } from '@/components/PageHeader';
import { FORM_COLUMN } from '@/components/layout';
import { BackLink } from '@/components/DetailRow';
import { CheckboxField, FormActions, FormError, TextareaField, TextField } from '@/components/form';
import { useCreateProduct } from '@/data/hooks';
import { BrandSelect, CategorySelect } from './BrandCategoryFields';

const BASE = '/app/admin/productos';

/**
 * Create a catalog product (admin). RHF + zodResolver against the shared
 * `createProductSchema`. Brand and category are picked from the existing ones (there are no
 * brand/category management screens in this slice); SKUs, prices, and per-SKU
 * availability are a later slice.
 */
export function ProductCreatePage() {
  const navigate = useNavigate();
  const mutation = useCreateProduct();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateProductInput>({
    resolver: zodResolver(createProductSchema),
    defaultValues: { brandId: '', categoryId: '', name: '', description: '', active: true },
  });

  const onSubmit = handleSubmit((values) => {
    mutation.mutate(values, {
      onSuccess: (created) => navigate(`${BASE}/${created.productId}`),
    });
  });

  return (
    <div className={`flex flex-col gap-6 ${FORM_COLUMN}`}>
      <div className="flex flex-col gap-2">
        <BackLink to={BASE}>Productos</BackLink>
        <PageHeader title="Nuevo producto" description="Agrega un producto al catálogo." />
      </div>

      <Card>
        <CardContent className="pt-5">
          <form onSubmit={onSubmit} className="flex flex-col gap-5" noValidate>
            <FormError error={mutation.error} />

            <TextField
              label="Nombre"
              required
              placeholder="Valvoline All-Climate 20W-50"
              error={errors.name?.message}
              {...register('name')}
            />
            <TextareaField
              label="Descripción"
              placeholder="Detalle opcional del producto."
              error={errors.description?.message}
              {...register('description')}
            />
            <div className="grid gap-5 sm:grid-cols-2">
              <BrandSelect required error={errors.brandId?.message} {...register('brandId')} />
              <CategorySelect required error={errors.categoryId?.message} {...register('categoryId')} />
            </div>
            <CheckboxField
              label="Activo"
              hint="Los productos inactivos no se muestran en el catálogo."
              {...register('active')}
            />

            <FormActions>
              <Button type="button" variant="outline" onClick={() => navigate(BASE)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending ? 'Guardando…' : 'Crear producto'}
              </Button>
            </FormActions>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
